import {combineReducers} from 'redux';
import {
    success as successNotification,
    error as errorNotification,
} from 'react-notification-system-redux';

import api from 'utils/api';
import {gettext, interpolate, ngettext} from 'utils/text';
import reverseUrl from 'utils/urls';


const SET_BASKET_DETAILS = 'cart/SET_BASKET_DETAILS';
const SET_BASKET_LINES = 'cart/SET_BASKET_LINES';
const SET_LOADING = 'cart/SET_LOADING';
const SET_SHIPPING_DETAILS = 'cart/SET_SHIPPING_DETAILS';


function isLoading(state = false, action) {
    switch (action.type) {
        case SET_LOADING:
            return action.status;

        default:
            return state;
    }
}

function details(state = {}, action) {
    switch (action.type) {
        case SET_BASKET_DETAILS:
            return {
                ...state,
                id: action.id,
                status: action.status,
                total_excl_tax: action.total_excl_tax,
                total_excl_tax_excl_discounts: action.total_excl_tax_excl_discounts,
                total_incl_tax: action.total_incl_tax,
                total_incl_tax_excl_discounts: action.total_incl_tax_excl_discounts,
                total_tax: action.total_tax,
                currency: action.currency,
                voucher_discounts: action.voucher_discounts,
                offer_discounts: action.offer_discounts,
                is_tax_known: action.is_tax_known,
            };
        case SET_SHIPPING_DETAILS:
            return {
                ...state,
                shipping_code: action.code,
                shipping_name: action.name,
                shipping_price: action.price,
            };

        default:
            return state;
    }
}

function lines(state = [], action) {
    switch (action.type) {
        case SET_BASKET_LINES:
            return action.lines;

        default:
            return state;
    }
}

export function getNotificationOptions(title, message) {
    return {
        title,
        message,
        position: 'tr',
        autoDismiss: parseFloat(DJ_CONST.ADD_TO_CART_DURATION),
    };
}

export default combineReducers({
    lines,
    details,
    isLoading,
});

// plain action
const setLoading = status => ({type: SET_LOADING, status});
const setBasketDetails = data => ({type: SET_BASKET_DETAILS, ...data});
const setBasketLines = linesList => ({type: SET_BASKET_LINES, lines: linesList});
const setShippingDetails = data => ({type: SET_SHIPPING_DETAILS, ...data});

// thunk actions
/**
 * Fetch current basket details, shipping details and lines.
 */
export const fetchCart = (updateLoading = true) => async (dispatch) => {
    if (updateLoading) {
        dispatch(setLoading(true));
    }
    let response;
    try {
        response = await api.userBasketDetails.fetch();
    } catch (err) {
        if (updateLoading) {
            dispatch(setLoading(false));
        }
        dispatch(errorNotification(getNotificationOptions(null, gettext('Failed to load cart'))));
        return;
    }

    dispatch(setBasketDetails(response));
    const basketID = response.id;

    try {
        response = await api.basketShippingMethods.fetch();
    } catch (err) {
        if (updateLoading) {
            dispatch(setLoading(false));
        }
        dispatch(errorNotification(getNotificationOptions(null, gettext('Failed to load cart'))));
        return;
    }

    // currently we only have one shipping method
    // so by default the first one (Standard) is always chosen
    dispatch(setShippingDetails(response[0]));

    // now fetch lines also, for showing cart content
    try {
        response = await api.basketLines.fetch({pk: basketID});
    } catch (err) {
        if (updateLoading) {
            dispatch(setLoading(false));
        }
        dispatch(errorNotification(getNotificationOptions(null, gettext('Failed to load cart'))));
        return;
    }

    dispatch(setBasketLines(response));

    // finally done
    if (updateLoading) {
        dispatch(setLoading(false));
    }
};


/**
 * Add new item to cart via OscarAPI.
 *  Show notification and clear it after a set amount of seconds.
 *
 * @param {Object} productId - Product ID to add to cart.
 * @param {Number} quantity - Amount to add to cart.
 * @param {String} title - Product title.
 */
export const addItem = (productId, title, quantity = 1) => async (dispatch) => {
    // We do validation before we get here and should also do it on server side, no need to do it again here
    dispatch(setLoading(true));

    // fetch product details
    quantity = parseInt(quantity, 10); // eslint-disable-line no-param-reassign
    const absQuantity = Math.abs(quantity);
    let text;
    try {
        await api.basketAddProductUrl.post(null, {
            url: reverseUrl('product-detail', productId),
            quantity,
        });
    } catch (err) {
        // Show a notification
        if (err.statusCode === 406) {
            dispatch(errorNotification(getNotificationOptions(title, gettext('Product out of stock'))));
        } else {
            if (quantity > 0) {
                text = ngettext(
                    'Failed to add %(quantity)s item to cart',
                    'Failed to add %(quantity)s items to cart',
                    quantity,
                );
                text = interpolate(text, {quantity}, true);
            } else {
                text = ngettext(
                    'Failed to remove %(quantity)s item from cart',
                    'Failed to remove %(quantity)s items from cart',
                    absQuantity,
                );
                text = interpolate(text, {quantity: absQuantity}, true);
            }
            const notification = getNotificationOptions(title, text);
            dispatch(errorNotification(notification));
        }

        // wait for cart update to be done.
        await dispatch(fetchCart());
        dispatch(setLoading(false));
        return;
    }

    // Show a notification
    if (quantity > 0) {
        text = ngettext('%(quantity)s item added to cart', '%(quantity)s items added to cart', quantity);
        text = interpolate(text, {quantity}, true);
    } else {
        text = ngettext('%(quantity)s item removed from cart', '%(quantity)s items removed from cart', absQuantity);
        text = interpolate(text, {quantity: absQuantity}, true);
    }
    const notification = getNotificationOptions(title, text);
    dispatch(successNotification(notification));

    // Update cart
    await dispatch(fetchCart());
    dispatch(setLoading(false));
};

export const removeItem = line => async (dispatch, getState) => {
    dispatch(setLoading(true));

    try {
        const basketId = getState().basket.details.id;
        await api.lineDetail.del({basketId, lineId: parseInt(line.pk, 10)});
        const notification = getNotificationOptions(line.product.title, gettext('Item removed from the cart'));
        dispatch(successNotification(notification));
    } catch (err) {
        const notification = getNotificationOptions(line.product.title, gettext('Failed to remove item from cart'));
        dispatch(errorNotification(notification));
    }

    // Update cart
    await dispatch(fetchCart());
    dispatch(setLoading(false));
};



// WEBPACK FOOTER //
// ./src/ducks/basket.js